import React, { useEffect, useState } from "react";

import { clone, pickAll } from "ramda";

import { VERIFF_STATUS } from "constants/process";
import CountriesProvider from "contexts/countriesCtx";
import { useStepsProgress, useUser } from "hooks";
import { KycInd } from "types/kyc";

import { isSanctionedCountryQuestionsRequired } from "./helpers";
import { AddressDetails } from "./ProofOfAddress/AddressDetails";
import { CarriedOverConfirmation } from "./ProofOfAddress/CarriedOverConfirmation";
import { DocumentsLanguage } from "./ProofOfAddress/DocumentsLanguage";
import { shouldShowRenewalAddressConfirmation } from "./ProofOfAddress/helpers";
import { UploadDocuments } from "./ProofOfAddress/UploadDocuments";
import { ProofOfIdentityCreated } from "./ProofOfIdentity/ProofOfIdentityCreated";
import { ProofOfIdentityInit } from "./ProofOfIdentity/ProofOfIdentityInit";
import { SanctionedCountryQuestions } from "./SanctionedCountryQuestions";
import { KycSummary } from "./Summary";

type Props = {
  kycData: KycInd;
};

export const IndividualKyc = ({ kycData }: Props) => {
  const { data: userData } = useUser();
  const {
    stepsProgress: { currentScreen },
    setStepsPercent,
    setCurrentStep,
    goToPreviousScreen,
  } = useStepsProgress();
  const [showAddressDetails, setShowAddressDetails] = useState(false);
  const { nationality, addressCountry } = !!kycData
    ? pickAll(["nationality", "addressCountry"], clone(kycData))
    : { nationality: "", addressCountry: "" };
  const showRenewalAddressConfirmation = shouldShowRenewalAddressConfirmation({
    currentKycStatus: userData?.kyc.currentStatus,
    currentKycExpiresAt: userData?.kyc.expiresAt,
    draftKycStatus: kycData?.status,
    poaCarriedOver: kycData?.poaCarriedOver,
  });
  const hasCompletedVeriff =
    kycData.veriffStatus === VERIFF_STATUS.approved || kycData.veriffStatus === VERIFF_STATUS.submitted;

  useEffect(() => {
    switch (currentScreen) {
      case 2: {
        setStepsPercent(40);
        break;
      }
      case 3: {
        setCurrentStep(2);
        setStepsPercent(55);
        break;
      }
      case 4: {
        setCurrentStep(2);
        setStepsPercent(60);
        break;
      }
      case 5: {
        setCurrentStep(2);
        setStepsPercent(75);
        break;
      }
      case 6: {
        setCurrentStep(3);
        setStepsPercent(80);
        break;
      }
      case 7: {
        setCurrentStep(3);
        setStepsPercent(100);
        break;
      }
    }
  }, [currentScreen]);

  useEffect(() => {
    if (currentScreen !== 3 || !showRenewalAddressConfirmation) {
      setShowAddressDetails(false);
    }
  }, [currentScreen, showRenewalAddressConfirmation]);

  const renderBody = () => {
    switch (currentScreen) {
      case 1:
        return <ProofOfIdentityInit />;
      case 2:
        if (!hasCompletedVeriff) {
          return <ProofOfIdentityInit />;
        }

        return <ProofOfIdentityCreated />;
      case 3:
        if (!showAddressDetails && showRenewalAddressConfirmation) {
          return <CarriedOverConfirmation mode='renewal' onDeclineToEdit={() => setShowAddressDetails(true)} />;
        }

        if (!showAddressDetails && kycData.poaCarriedOver) {
          return <CarriedOverConfirmation />;
        }

        return <AddressDetails />;
      case 4:
        if (!isSanctionedCountryQuestionsRequired([nationality, addressCountry])) {
          goToPreviousScreen();
          return null;
        }
        return <SanctionedCountryQuestions />;
      case 5:
        return <DocumentsLanguage />;
      case 6:
        return <UploadDocuments />;
      case 7:
        return <KycSummary />;
      default:
        return null;
    }
  };

  return <CountriesProvider>{renderBody()}</CountriesProvider>;
};
